import { useTranslation } from "react-i18next";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { cn } from "@/utils/cn";
import { useDropzone } from "react-dropzone";
import { useFormik, type FormikProps } from "formik";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as yup from "yup";
import { useGetBranchesQuery } from "@/services/(company)/branchesApi";
import { Textarea } from "@/components/ui/textarea";
import UploadViaQRCode from "@/components/UploadViaQRCode";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageIcon, ImageUp, X } from "lucide-react";
import { mbToBytes } from "@/utils/helper";
import {
  useCompanyGetTeamDetailsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
} from "@/services/(company)/api.teams";
import toast from "react-hot-toast";
import { TEAM_STEPS } from "..";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "@/store/slices/user.slice";
import { hTabKey } from "@/components/HorizontalTabs";
import { Spinner } from "@/components/ui/v2/spinner";
import { PATHS } from "@/routes/paths";
import { PermissionButton } from "@/components/ui/permission-button";

const buildFormSchema = (t: (key: string) => string) =>
  yup.object().shape(
    {
      name: yup
        .string()
        .required(t("teams:form.name_required"))
        .min(3, t("teams:form.name_min"))
        .max(50, t("teams:form.name_max")),
      description: yup
        .string()
        .required(t("teams:form.description_required"))
        .min(10, t("teams:form.description_min")),
      branchId: yup.string().required(t("teams:form.branch_required")),
      image: yup
        .mixed()
        .test("is-valid", t("teams:form.image_required"), (val, ctx) => {
          // If we have a fileId, image isn't required
          if (ctx.parent.fileId) return true;
          // Otherwise, check if image exists and is the right type
          return val instanceof File || typeof val === "string";
        }),
      fileId: yup.string().nullable(),
    },
    [["image", "fileId"]],
  );

export const TEAM_ID_SEARCHPARAM_KEY = "tId";

type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;
const fieldNames: Record<keyof Fields, keyof Fields> = {
  name: "name",
  description: "description",
  branchId: "branchId",
  image: "image",
  fileId: "fileId",
};

export const CreateTeamForm = ({
  teamId,
  isUpdating,
}: {
  teamId?: string;
  isUpdating: boolean;
}) => {
  const { t } = useTranslation(["common", "company", "teams"]);
  const FormSchema = buildFormSchema(t);
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser).user;
  const {
    data: { data: teamDetails } = {},
    isFetching: isFetchingTeamDetails,
  } = useCompanyGetTeamDetailsQuery(
    { teamId: Number(teamId!) },
    { skip: !isUpdating },
  );

  // const [searchParams, setSearchParams] = useSearchParams();
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation();
  const [updateTeam, { isLoading: isUpdatingTeam }] = useUpdateTeamMutation();

  const { data: branchesData, isFetching: isFetchingBranches } =
    useGetBranchesQuery();

  const formik = useFormik<Fields>({
    enableReinitialize: true,
    validationSchema: FormSchema,
    initialValues: {
      name: teamDetails?.name || "",
      image: teamDetails?.image || "",
      description: teamDetails?.description || "",
      branchId: teamDetails?.branchId
        ? String(teamDetails?.branchId)
        : currentUser?.branchId
          ? String(currentUser.branchId)
          : "",
      fileId: "",
    },
    onSubmit: async (values) => {
      const payload = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        // If fileId from QR upload is present, skipping upload field image
        if (key === fieldNames.image && values.fileId) return;
        if (value instanceof File) payload.append(key, value);
        else if (value !== null && value !== undefined)
          payload.append(key, String(value));
      });
      if (teamId && isUpdating) {
        await updateTeam({ teamId, payload })
          .unwrap()
          .then((res) => {
            toast.success(res?.message);
          });
      } else {
        await createTeam(payload)
          .unwrap()
          .then((res) => {
            const newTeamId = res.data.id;
            if (newTeamId) {
              navigate(
                {
                  pathname: PATHS.COMPANY.COMPANY_TEAMS_PATHS.EDIT(newTeamId),
                  search: `?${hTabKey}=${TEAM_STEPS.TeamFormation}`,
                },
                { replace: true },
              );
            }
            toast.success(res?.message);
          })
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      }
    },
  });

  if (isFetchingTeamDetails) return <Spinner />;
  return (
    <form onSubmit={formik.handleSubmit} className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-scroll">
        <SectionTitle title={t("company:employees.basic_information")} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Field>
            <FieldLabel>{t("teams:form.team_name_label")}</FieldLabel>
            <Input
              id={fieldNames.name}
              value={formik.values.name}
              onChange={formik.handleChange}
              placeholder={t("common:placeholders.marketing_team")}
              className={cn(
                formik.touched[fieldNames.name] &&
                  formik.errors[fieldNames.name] &&
                  "border-danger",
              )}
            />
            <FieldError>
              {formik.touched[fieldNames.name] &&
                (formik.errors[fieldNames.name] as string)}
            </FieldError>
          </Field>

          <Field>
            <FieldLabel htmlFor={fieldNames.branchId}>
              {t("teams:form.branch_label")}
            </FieldLabel>
            <Select
              disabled={isFetchingBranches}
              value={formik.values.branchId}
              onValueChange={(value) =>
                formik.setFieldValue(fieldNames.branchId, value)
              }
            >
              <SelectTrigger id={fieldNames.branchId}>
                <SelectValue
                  placeholder={t("company:shared.select_a_branch")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {branchesData?.data?.map((branch) => (
                    <SelectItem key={branch.id} value={String(branch.id)}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <FieldError>
              {formik.touched[fieldNames.branchId] &&
                (formik.errors[fieldNames.branchId] as string)}
            </FieldError>
          </Field>

          <Field className="col-span-2">
            <FieldLabel>{t("teams:form.description_label")}</FieldLabel>
            <Textarea
              id={fieldNames.description}
              value={formik.values.description}
              onChange={formik.handleChange}
              placeholder={t("common:a11y.team_description")}
              className={cn(
                formik.touched[fieldNames.description] &&
                  formik.errors[fieldNames.description] &&
                  "border-danger",
              )}
            />
            <FieldError>
              {formik.touched[fieldNames.name] &&
                (formik.errors[fieldNames.description] as string)}
            </FieldError>
          </Field>
          <TeamLogoUploadField formik={formik} fieldName={fieldNames.image} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="reset"
          variant="outline"
          onClick={() => navigate(PATHS.COMPANY.COMPANY_TEAMS_PATHS.ROOT)}
        >
          {t("teams:form.cancel")}
        </Button>
        <PermissionButton
          type="submit"
          section={PLATFORM_SECTION_ENUM.TEAMS}
          action={isUpdating ? "edit" : "create"}
          isLoading={isCreating || isUpdatingTeam}
        >
          {t("teams:form.save_and_next")}
        </PermissionButton>
      </div>
    </form>
  );
};

const TeamLogoUploadField = ({
  formik,
  fieldName,
}: {
  formik: FormikProps<Fields>;
  fieldName: keyof Fields;
}) => {
  const { t } = useTranslation(["teams"]);
  const [preview, setPreview] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    maxSize: mbToBytes(5),
    multiple: false,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        formik.setFieldValue(fieldName, file);
        setPreview(URL.createObjectURL(file));
      }
    },
  });

  const handleClear = () => {
    formik.setFieldValue(fieldName, null);
    setPreview(null);
  };

  // Handle initial values or string URLs from the server
  useEffect(() => {
    const value = formik.values[fieldName];
    if (typeof value === "string") setPreview(value);
    return () => {
      if (
        preview &&
        typeof preview === "string" &&
        preview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [formik.values, fieldName]);

  return (
    <div className="w-full">
      <FieldLabel className="mb-2">{t("teams:form.logo_label")}</FieldLabel>
      {preview ? (
        <div className="relative w-max h-44 lg:h-80 bg-gray-50 rounded-sm overflow-hidden flex items-center justify-center border">
          <img
            src={preview}
            alt={t("teams:form.preview_alt")}
            className="h-full w-full object-contain"
          />
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 end-2 p-1 text-white bg-black/50 rounded-full"
          >
            <X size={18} />
          </button>
        </div>
      ) : (
        <>
          <div
            {...getRootProps()}
            className={`
              border border-spacing-11 border-dashed border-primary p-5 text-center cursor-pointer transition-colors mb-2.5 rounded-sm h-44 lg:h-80 bg-primary/10
              ${isDragActive ? "bg-primary/10 border-primary" : "bg-transparent border-gray-300"}
              ${formik.touched[fieldName] && formik.errors[fieldName] ? "border-red-500" : ""}
            `}
          >
            <input {...getInputProps()} />
            <div className="h-full py-4 flex flex-col justify-center items-center">
              <ImageIcon size={42} strokeWidth={1.2} className="text-primary" />
              <h6 className="font-semibold mt-2">
                {t("teams:form.upload_logo")}
              </h6>
              <p className="text-sm text-black/50">
                {t("teams:form.upload_hint")}
              </p>
              {isDragActive && (
                <p className="text-sm text-primary font-medium">
                  {t("teams:form.drop_image_here")}
                </p>
              )}
              <Button type="button" className="mt-4">
                <ImageUp size={22} className="me-1" />{" "}
                {t("teams:form.upload_image")}
              </Button>
            </div>
          </div>
          <UploadViaQRCode
            fileType="image/*"
            moduleType={PLATFORM_SECTION_ENUM.TEAMS}
            onUploadComplete={(data) => {
              console.info("QR upload complete", data);
              // For image preview
              formik.setFieldValue(fieldName, data.fileUrls.frontSideFileUrl);
              // For backend. If QR upload provides a fileId, we skip the image field in the payload and just send the fileId
              formik.setFieldValue(
                fieldNames.fileId,
                data.fileIds.frontSideFileId,
              );
            }}
          />
        </>
      )}
      {formik.touched[fieldName] && formik.errors[fieldName] && (
        <p className="text-red-500 text-xs mt-1">
          {String(formik.errors[fieldName])}
        </p>
      )}
    </div>
  );
};
